import { useState, useRef } from 'react';

const ROLES = [
    'Supplier Manager (SM)',
    'WS Account Manager',
    'GRC',
    'Finance',
    'IT',
    'TMO',
    'Supplier',
    'Other',
];

const ISSUE_CATEGORIES = [
    'Registration (account creation, W-9, TIN match)',
    'Qualification (enrollment CFP)',
    'Contract execution',
    'Payment and fees',
    'VMS access or provisioning',
    'Profile management',
    'Other',
];

const PROGRAMS = [
    'All Programs',
    'Alabama', 'Arkansas', 'Connecticut', 'DC', 'Florida', 'Georgia',
    'Hawaii', 'Idaho', 'Indiana', 'Iowa', 'New Hampshire', 'New Jersey',
    'New Mexico', 'North Carolina', 'North Dakota', 'Sourcewell All Categories',
    'Sourcewell IT', 'Utah', 'VITA', 'WMATA',
];

const SEVERITY_LEVELS = [
    { value: 'P1', label: 'P1 Critical \u2014 Portal fully down or unusable' },
    { value: 'P2', label: 'P2 High \u2014 A core function or key integration is broken, but a workaround exists' },
    { value: 'P3', label: 'P3 Medium \u2014 Minor bug or single-workflow issue, workaround available' },
    { value: 'P4', label: 'P4 Low \u2014 Cosmetic or nice-to-fix, no real business impact' },
    { value: 'Unknown', label: 'Unknown' },
];

const BROWSERS = [
    'Chrome',
    'Microsoft Edge',
    'Firefox',
    'Safari',
    'Mobile browser (iOS or Android)',
    'Not applicable / unknown',
];

const ALLOWED_EXTENSIONS = [
    '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.pdf',
    '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.svg',
    '.mp4', '.mov', '.avi', '.mp3', '.wav',
];

const MAX_FILES = 3;

function EscalationForm({ userName = '', userEmail = '' }) {
    const [form, setForm] = useState({
        name: userName,
        email: userEmail,
        role: '',
        issueCategory: '',
        issueHeadline: '',
        issueDescription: '',
        affectedPrograms: [],
        dateTimeObserved: '',
        severityLevel: '',
        browser: '',
    });
    const [files, setFiles] = useState([]);
    const [programSearch, setProgramSearch] = useState('');
    const [showProgramDropdown, setShowProgramDropdown] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [ticketId, setTicketId] = useState('');
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);
    const programRef = useRef(null);

    const filteredPrograms = PROGRAMS.filter(
        p => p.toLowerCase().includes(programSearch.toLowerCase()) &&
            !form.affectedPrograms.includes(p)
    );

    const handleChange = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const addProgram = (program) => {
        setForm(prev => ({
            ...prev,
            affectedPrograms: [...prev.affectedPrograms, program]
        }));
        setProgramSearch('');
        setShowProgramDropdown(false);
    };

    const removeProgram = (program) => {
        setForm(prev => ({
            ...prev,
            affectedPrograms: prev.affectedPrograms.filter(p => p !== program)
        }));
    };

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        const validFiles = newFiles.filter(f => {
            const ext = '.' + f.name.split('.').pop().toLowerCase();
            return ALLOWED_EXTENSIONS.includes(ext);
        });
        setFiles(prev => [...prev, ...validFiles].slice(0, MAX_FILES));
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);

        try {
            // TODO: POST to Azure Function API endpoint that writes to SharePoint
            const payload = {
                ...form,
                affectedPrograms: form.affectedPrograms.join('; '),
            };

            // Placeholder: replace with actual API endpoint
            const API_URL = process.env.REACT_APP_API_URL || '/api/submit';
            const formData = new FormData();
            formData.append('data', JSON.stringify(payload));
            files.forEach((file, i) => formData.append(`file${i}`, file));

            const response = await fetch(API_URL, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errBody = await response.json().catch(() => null);
                throw new Error(errBody?.error || `Submit failed: ${response.statusText}`);
            }

            const result = await response.json();
            setTicketId(result.ticketId || '');
            setSubmitted(true);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setForm({
            name: userName,
            email: userEmail,
            role: '',
            issueCategory: '',
            issueHeadline: '',
            issueDescription: '',
            affectedPrograms: [],
            dateTimeObserved: '',
            severityLevel: '',
            browser: '',
        });
        setFiles([]);
        setSubmitted(false);
        setTicketId('');
        setError('');
    };

    if (submitted) {
        return (
            <div className="max-w-2xl mx-auto py-16 px-4 text-center">
                <div className="bg-white rounded-lg shadow-md p-8">
                    <div className="text-green-600 text-5xl mb-4">&#10003;</div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Escalation Submitted</h2>
                    {ticketId && (
                        <p className="text-lg font-mono bg-gray-100 inline-block px-4 py-1 rounded mb-3">{ticketId}</p>
                    )}
                    <p className="text-gray-600 mb-6">
                        Your escalation has been recorded. The hypercare task force will be notified.
                    </p>
                    <button
                        onClick={resetForm}
                        className="bg-blue-600 text-white px-6 py-2.5 rounded-md hover:bg-blue-700 transition font-medium"
                    >
                        Submit Another
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto py-8 px-4">
            <div className="bg-white rounded-lg shadow-md">
                <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg">
                    <h1 className="text-xl font-bold">CAI Supplier Portal - Hypercare Escalation Form</h1>
                    <p className="text-blue-100 text-sm mt-1">
                        Use this form to escalate CAI Supplier Portal issues during hypercare.
                        For supplier questions that do not require escalation, continue to use SM.PortalHelp.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    {/* Name & Email from SSO — read-only */}
                    <div className="bg-gray-50 border border-gray-200 px-4 py-3 rounded-md text-sm text-gray-700">
                        <span className="font-medium">{form.name}</span> &mdash; {form.email}
                    </div>

                    {/* 2. Role */}
                    <Field label="Role" required helpText="Your role, or the role you are submitting on behalf of.">
                        <select value={form.role} onChange={handleChange('role')} required className="input">
                            <option value="">Select a role...</option>
                            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </Field>

                    {/* 3. Issue Category */}
                    <Field label="Issue Category" required helpText="Select the area where the issue occurred. If more than one applies, please create a separate ticket for each.">
                        <select value={form.issueCategory} onChange={handleChange('issueCategory')} required className="input">
                            <option value="">Select a category...</option>
                            {ISSUE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Field>

                    {/* 4. Issue Headline */}
                    <Field label="Issue Headline" required helpText="Please enter a few words/headline of the issue.">
                        <input
                            type="text"
                            value={form.issueHeadline}
                            onChange={handleChange('issueHeadline')}
                            required
                            placeholder="Brief headline of the issue"
                            className="input"
                        />
                    </Field>

                    {/* 5. Issue Description */}
                    <Field label="Issue Description" required helpText="Describe what happened and share any steps to reproduce the error, if known. Include Supplier ID and Supplier Name where applicable.">
                        <textarea
                            value={form.issueDescription}
                            onChange={handleChange('issueDescription')}
                            required
                            rows={4}
                            placeholder="Describe the issue in detail..."
                            className="input resize-y"
                        />
                    </Field>

                    {/* 6. Affected Program */}
                    <Field label="Affected Program" required helpText="Select the program(s) this issue affects.">
                        <div className="relative" ref={programRef}>
                            {form.affectedPrograms.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    {form.affectedPrograms.map(p => (
                                        <span key={p} className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-2.5 py-0.5 rounded-full">
                                            {p}
                                            <button type="button" onClick={() => removeProgram(p)} className="ml-1.5 text-blue-600 hover:text-blue-800 font-bold">&times;</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                            <input
                                type="text"
                                value={programSearch}
                                onChange={(e) => { setProgramSearch(e.target.value); setShowProgramDropdown(true); }}
                                onFocus={() => setShowProgramDropdown(true)}
                                placeholder="Start typing to search programs..."
                                className="input"
                            />
                            {showProgramDropdown && filteredPrograms.length > 0 && (
                                <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-48 overflow-y-auto shadow-lg">
                                    {filteredPrograms.map(p => (
                                        <li
                                            key={p}
                                            onClick={() => addProgram(p)}
                                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                        >
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {/* Hidden required input to enforce at least one program */}
                            <input
                                type="text"
                                required
                                value={form.affectedPrograms.length > 0 ? 'valid' : ''}
                                onChange={() => {}}
                                className="sr-only"
                                tabIndex={-1}
                            />
                        </div>
                    </Field>

                    {/* 7. Date and Time Observed */}
                    <Field label="Date and Time Observed" required helpText="Enter the date and approximate time the issue was observed, with time zone.">
                        <input
                            type="text"
                            value={form.dateTimeObserved}
                            onChange={handleChange('dateTimeObserved')}
                            required
                            placeholder="e.g., 10/06/2026, 2:15 PM ET"
                            className="input"
                        />
                    </Field>

                    {/* 8. Severity Level */}
                    <Field label="Severity Level Impact" required>
                        <select value={form.severityLevel} onChange={handleChange('severityLevel')} required className="input">
                            <option value="">Select severity...</option>
                            {SEVERITY_LEVELS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                    </Field>

                    {/* 9. Supporting Attachment */}
                    <Field label="Supporting Attachment" helpText={`Attach up to ${MAX_FILES} files (screenshots, documents). Max 3 files.`}>
                        {files.length > 0 && (
                            <ul className="mb-2 space-y-1">
                                {files.map((f, i) => (
                                    <li key={i} className="flex items-center justify-between bg-gray-50 px-3 py-1.5 rounded text-sm">
                                        <span className="truncate">{f.name}</span>
                                        <button type="button" onClick={() => removeFile(i)} className="text-red-500 hover:text-red-700 ml-2 font-bold">&times;</button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {files.length < MAX_FILES && (
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={handleFileChange}
                                accept={ALLOWED_EXTENSIONS.join(',')}
                                className="text-sm text-gray-600"
                            />
                        )}
                    </Field>

                    {/* 10. Browser / Device */}
                    <Field label="Browser / Device Used" helpText="Please select the browser used.">
                        <select value={form.browser} onChange={handleChange('browser')} className="input">
                            <option value="">Select browser...</option>
                            {BROWSERS.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                    </Field>

                    {/* Submit */}
                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-blue-600 text-white py-2.5 rounded-md hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Submitting...' : 'Submit Escalation'}
                        </button>
                    </div>
                </form>
            </div>

            <p className="text-center text-xs text-gray-400 mt-4">
                This form is intended for internal CAI use only. Do not distribute externally.
            </p>
        </div>
    );
}

function Field({ label, required, helpText, children }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
                {label}{required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {helpText && <p className="text-xs text-gray-500 mb-1.5">{helpText}</p>}
            {children}
        </div>
    );
}

export default EscalationForm;
